const guide = `# Documentación del Portal del Proveedor

## Resumen

El Portal del Proveedor permite a los proveedores solicitar eSIMs, gestionar inventario, asignar eSIMs a clientes y hacer seguimiento del estado de activación. Esta guía describe el flujo de trabajo completo, las APIs disponibles, los requisitos de la interfaz y el comportamiento del sistema.

**Ruta base de la API:** \`https://r4lsolutions.com/backend/api/provider\`

> Todos los endpoints requieren un Token de Autenticación de Proveedor válido.

---

# Mapeo de Estado de eSIM

El backend devuelve valores de estado internos que deben mostrarse con etiquetas comprensibles para el usuario.

| Estado del backend       | Etiqueta mostrada      |
| ------------------------ | ---------------------- |
| \`waiting_for_assignment\` | En stock               |
| \`pre_service\`            | Esperando activación   |
| \`in_service\`             | Activo                 |
| \`terminated\`             | Terminado              |

---

# Mapeo de Estado de Solicitud

| Estado     | Significado                                       |
| ---------- | --------------------------------------------- |
| \`pending\`  | Solicitud enviada y pendiente de aprobación   |
| \`approved\` | Solicitud aprobada y eSIMs añadidos al inventario |
| \`rejected\` | Solicitud rechazada                           |

---

# Flujo de Trabajo del Proveedor

\`\`\`text
Solicitar SIMs
      ↓
Aprobación de solicitud
      ↓
eSIM añadido al inventario
      ↓
Asignar eSIM al cliente
      ↓
Generar código QR
      ↓
El cliente activa la eSIM
      ↓
Sincronizar estado
      ↓
La eSIM queda activa
\`\`\`

---

# 1. Solicitar eSIMs

Los proveedores pueden enviar solicitudes de nuevo inventario de eSIMs.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Cuerpo de la solicitud

\`\`\`json
{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}
\`\`\`

### Validación

| Campo    | Tipo   | Requerido | Notas               |
| -------- | ------ | -------- | ------------------- |
| quantity | Número | Sí       | Rango: 1–500        |
| note     | Texto  | No       | Comentarios adicionales |

### Comportamiento esperado

* Crear una nueva solicitud.
* El estado de la solicitud se establece automáticamente en \`pending\`.
* La solicitud recién creada debe aparecer en la Lista de Solicitudes.

---

# 2. Ver mis solicitudes de SIM

Los proveedores pueden hacer seguimiento de las solicitudes enviadas y monitorizar el estado de aprobación.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Ejemplo

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests?page=1&pageSize=20&status=pending
\`\`\`

### Filtros disponibles

| Filtro    | Descripción                 |
| --------- | --------------------------- |
| status    | pending, approved, rejected |
| search    | Buscar por ID de solicitud  |
| startDate | Filtrar por fecha de creación |
| endDate   | Filtrar por fecha de creación |

### API de estadísticas

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests/stats
\`\`\`

### Requisitos de la interfaz

Mostrar:

* ID de solicitud
* Cantidad
* Notas
* Fecha de creación
* Insignia de estado

### Insignias de estado

* Pendiente
* Aprobado
* Rechazado

> Una vez aprobada una solicitud, las eSIMs asociadas quedan automáticamente disponibles en la sección de Inventario.

---

# 3. Gestión del inventario de eSIMs

El inventario muestra todas las eSIMs asignadas al proveedor.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims
\`\`\`

### Ejemplo

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims?assigned=false&status=pre_service&page=1&pageSize=20
\`\`\`

### Filtros disponibles

| Filtro    | Descripción       |
| --------- | ----------------- |
| assigned  | true / false      |
| status    | Estado de la eSIM |
| search    | Búsqueda por ICCID |
| startDate | Filtro por rango de fechas |
| endDate   | Filtro por rango de fechas |

### API de estadísticas

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims/stats
\`\`\`

### Campos de respuesta

| Campo           | Descripción                  |
| --------------- | ---------------------------- |
| iccid           | Identificador único de la eSIM |
| activation_code | Código de activación (cadena LPA) |
| status          | Estado actual de la eSIM     |
| customer_id     | Cliente asignado             |
| activated_at    | Marca de tiempo de activación |

### Requisitos de la interfaz

Mostrar:

* ICCID
* Estado
* Cliente asignado
* Fecha de activación
* Acciones

### Acciones

| Acción      | Disponibilidad          |
| ----------- | --------------------- |
| Assign      | eSIMs no asignadas    |
| Sync Status | Todos los registros del inventario |

### Vistas del inventario

**Disponibles para asignar**

\`\`\`text
assigned = false
\`\`\`

Muestra las eSIMs listas para asignar a clientes.

---

# 4. Asignar eSIM a un cliente

Este paso vincula una eSIM disponible a un cliente y genera las credenciales de activación.

### API

\`\`\`http
PATCH https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign
\`\`\`

### Cuerpo de la solicitud

\`\`\`json
{
  "customer_id": "cust_abc123"
}
\`\`\`

### Respuesta

Devuelve:

* Información de la eSIM asignada
* Código de activación (cadena LPA)

Ejemplo:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

---

## Requisitos del frontend tras la asignación

### Generar código QR

El \`activation_code\` devuelto debe convertirse en un código QR.

### Mostrar código de activación

Proporcionar el código de activación sin procesar como opción alternativa.

Ejemplo:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

### Funcionalidad de copiar

Permitir al proveedor copiar el código de activación.

### Importante

Asignar una eSIM **no la activa**.

El estado permanece:

\`\`\`text
Esperando activación
\`\`\`

hasta que el cliente instale el perfil de la eSIM.

---

# 5. Activación del cliente

No se requiere ninguna acción del proveedor durante la activación.

### Proceso

1. El proveedor comparte el código QR.
2. El cliente escanea el código QR en su dispositivo.
3. El operador instala el perfil de la eSIM.
4. El backend recibe la confirmación de activación.
5. El estado cambia automáticamente:

\`\`\`text
pre_service → in_service
\`\`\`

### Requisito del frontend

No se necesita ninguna llamada a la API.

El proveedor solo necesita actualizar o sincronizar el estado del inventario.

---

# 6. Sincronizar estado de activación

Permite a los proveedores obtener las últimas actualizaciones de activación del operador.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/esims/sync
\`\`\`

### Respuesta de ejemplo

\`\`\`json
{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}
\`\`\`

### Comportamiento del frontend

Tras una sincronización exitosa:

1. Recargar los datos del inventario.
2. Actualizar las insignias de estado.
3. Mostrar las marcas de tiempo de activación.
4. Mover las eSIMs activadas al estado Activo.

### Transición de estado

\`\`\`text
Esperando activación
          ↓
       Activo
\`\`\`

---

# Gestión de clientes

Los proveedores pueden gestionar clientes a través del módulo de clientes.

### API de lista de clientes

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers
\`\`\`

### API de estadísticas de clientes

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers/stats
\`\`\`

---

# Referencia completa de la API

## Solicitudes de SIM

| Método | Endpoint                           | Descripción            |
| ------ | ---------------------------------- | ---------------------- |
| POST   | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Crear nueva solicitud de SIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Listar solicitudes de SIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests/stats\` | Estadísticas de solicitudes |

---

## Inventario de eSIMs

| Método | Endpoint                             | Descripción             |
| ------ | ------------------------------------ | ----------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims\`                | Listar inventario de eSIMs |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims/stats\`          | Estadísticas del inventario |
| PATCH  | \`https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign\` | Asignar eSIM a cliente |
| POST   | \`https://r4lsolutions.com/backend/api/provider/esims/sync\`           | Sincronizar estado de activación |

---

## Clientes

| Método | Endpoint                        | Descripción         |
| ------ | ------------------------------- | ------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers\`       | Lista de clientes   |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers/stats\` | Estadísticas de clientes |

---

# Ciclo de vida de extremo a extremo

\`\`\`text
El proveedor solicita eSIMs
          ↓
Solicitud aprobada
          ↓
eSIM añadido al inventario
          ↓
El proveedor asigna la eSIM
          ↓
Código QR generado
          ↓
El cliente instala la eSIM
          ↓
El operador confirma la activación
          ↓
El proveedor sincroniza el estado
          ↓
La eSIM queda activa
\`\`\`

Este flujo de trabajo permite a los proveedores gestionar eficientemente el inventario, distribuir eSIMs a clientes y monitorizar el progreso de activación desde un único portal.
`;

export default guide;
